import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { getMyCaseReplayUrl, getSessionReplayUrl } from '../api/sessions';
import type { HandPath } from '../replay/buildTimeline';
import { loadReplayTimeline, type ReplayLoad } from '../replay/loadReplayTimeline';

/**
 * 3D hand trajectory for the focused participant (MULTI-PARTICIPANT-FOCUS),
 * reusing the replay's `HandTrajectory3D` + timeline builder. The path is
 * revealed up to the pinned moment (`tSec`) — click the trace and the hand
 * path draws to that instant, matching the session replay.
 *
 * The trajectory is the IMU-integrated *relative shape* (ZUPT-corrected) — a
 * best-effort spatial path, not metric-accurate absolute position (drift; see
 * ROADMAP ANALYSIS-MOTION-TRAJECTORY for the camera-pose version). Read as a
 * stream off the UI thread, like the replay (replay/loadReplayTimeline.ts);
 * a recording too long to rebuild the path says so instead.
 */

const HandTrajectory3D = lazy(() =>
  import('./HandTrajectory3D').then((m) => ({ default: m.HandTrajectory3D })),
);

interface Props {
  /** Required unless `fetchUrl` is given. */
  tenantId?: string;
  sessionRowId: string;
  /** Appended to the heading ("HAND PATH (3D) · name") when given. */
  participantName?: string;
  /** Session-elapsed seconds pinned on the trace, or null (= whole path). */
  tSec: number | null;
  /** Participant read-only: fetch via the case-scoped /me endpoint. Requires
   *  `instanceId`. */
  asParticipant?: boolean;
  instanceId?: string;
  /** Custom presigned-URL fetcher (e.g. the participant's own-session
   *  endpoint on the Dashboard). Overrides the tenant / case endpoints. */
  fetchUrl?: () => Promise<{ url: string }>;
  /** Render nothing while there's no drawable path (loading, no IMU data,
   *  or a failed load) — for placing the panel beside another view where
   *  an empty box would just take space. */
  hideWhenEmpty?: boolean;
}

export function HandTrajectoryPanel({
  tenantId,
  sessionRowId,
  participantName,
  tSec,
  asParticipant = false,
  instanceId,
  fetchUrl,
  hideWhenEmpty = false,
}: Props) {
  const [trajectories, setTrajectories] = useState<HandPath[] | null>(null);
  const [error, setError] = useState(false);
  const [skipped, setSkipped] = useState(false);
  // Callers pass an inline closure; keep it out of the effect deps so a
  // re-render doesn't re-download the recording. `sessionRowId` keys it.
  const fetchUrlRef = useRef(fetchUrl);
  fetchUrlRef.current = fetchUrl;

  useEffect(() => {
    let live = true;
    let load: ReplayLoad | null = null;
    setTrajectories(null);
    setError(false);
    setSkipped(false);
    (fetchUrlRef.current
      ? fetchUrlRef.current()
      : asParticipant && instanceId
        ? getMyCaseReplayUrl(instanceId, sessionRowId)
        : getSessionReplayUrl(tenantId ?? '', sessionRowId))
      .then(({ url }) => {
        if (!live) return null;
        load = loadReplayTimeline(url);
        return load.promise;
      })
      .then((tl) => {
        if (!live || !tl) return;
        setSkipped(Boolean(tl.trajectorySkipped));
        setTrajectories(tl.trajectories);
      })
      .catch(() => live && setError(true));
    return () => {
      live = false;
      load?.cancel();
    };
  }, [tenantId, sessionRowId, asParticipant, instanceId]);

  if (
    hideWhenEmpty &&
    (error ||
      trajectories === null ||
      !trajectories.some((h) => h.points.length > 1))
  ) {
    return null;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, letterSpacing: '.06em', color: 'text.secondary' }}
      >
        HAND PATH (3D){participantName ? ` · ${participantName}` : ''}
      </Typography>
      {error ? (
        <Alert severity="info" variant="outlined" sx={{ mt: 0.5 }}>
          Couldn’t load the hand path for this recording.
        </Alert>
      ) : trajectories === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : skipped ? (
        <Stack sx={{ py: 1.5, px: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Not shown for a recording this long: the 3D hand path is rebuilt
            for up to about half an hour of hand motion.
          </Typography>
        </Stack>
      ) : trajectories.length === 0 ? (
        <Stack sx={{ py: 1.5, px: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            No hand-motion (IMU) data in this recording.
          </Typography>
        </Stack>
      ) : (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
          <Suspense
            fallback={
              <Stack alignItems="center" sx={{ py: 4 }}>
                <CircularProgress size={22} />
              </Stack>
            }
          >
            <HandTrajectory3D
              trajectories={trajectories}
              tSec={tSec ?? Number.MAX_SAFE_INTEGER}
            />
          </Suspense>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1, pb: 0.5 }}>
            Click the trace to reveal the path up to that moment
          </Typography>
        </Box>
      )}
    </Box>
  );
}
